import * as React from 'react'

import {autobind} from '@base/decorators'

import {createOrCallElement} from './cloneOrCreate'
import {omitProps} from './props/matchProps'

/** withState is a HOC factory, which will store a property in it's state,
 * pass it down as a prop and update it whenever a callback is called.
 *
 * For example, to have a <TextInput> with controlled value,
 * you can create a StateHolder which will handle the value flow for you:
 *
 * ~~~ js
 * const ControlledInput = withState({
 *   prop: 'value',
 *   onChangeProp: 'onValueChange',
 * })(TextInput)
 * ~~~
 *
 * or just use a \`withValueState\` for that particular case
 * */
export const withState =
  ({
    displayName,

    /** Name of the prop to pass down (eg. 'value') */
    prop,

    /** Name of the prop with the callback changing the value (eg. 'onChange') */
    onChangeProp,

    /** Function mapping arguments passed to callback to a value */
    mapChange = (old, newValue) => newValue,

    /** Initial value to start with */
    initial,

    /** Name of the prop with initial value. Changes on this prop will also override current value. Defaults to the name of the prop. */
    semiControlledProp = prop,

    mapInitialValue,
  }: any) =>
  (Component: any) => {
    // eslint-disable-next-line react/no-unsafe
    return class StateHolder extends React.Component<{}, {value: any}> {
      static displayName =
        displayName || `withState(${prop})(${Component.displayName})`

      constructor(props) {
        super(props)
        this.state = {
          value: mapInitialValue ? mapInitialValue(props) : initial,
        }
      }

      // @ts-ignore
      @autobind
      handleChange(...args: Array<any>) {
        this.setState({value: mapChange(this.state.value, ...args)})
        if (this.props[onChangeProp]) this.props[onChangeProp](...args)
      }

      UNSAFE_componentWillMount() {
        if (
          semiControlledProp &&
          this.props[semiControlledProp] !== undefined
        ) {
          this.setState({value: this.props[semiControlledProp]})
        }
      }

      UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (
          semiControlledProp &&
          this.props[semiControlledProp] !== nextProps[semiControlledProp]
        ) {
          this.setState({value: nextProps[semiControlledProp]})
        }
      }

      render() {
        const props = omitProps(this.props, [
          prop,
          semiControlledProp,
          onChangeProp,
        ])
        return createOrCallElement(Component, {
          [prop]: this.state.value,
          [onChangeProp]: this.handleChange,
          ...props,
        })
      }
    }
  }

export const withValueState = (options: any = {}) =>
  withState({
    prop: 'value',
    onChangeProp: 'onValueChange',
    ...options,
  })

export const withOpenedState = (options: any = {}) =>
  withState({
    prop: 'opened',
    onChangeProp: 'onToggle',
    ...options,
  })
